/**
 * The request governor: when the platform says "slow down", new reads wait.
 *
 * The client must never turn one refusal into many
 * (docs/plans/resilience-and-performance.md, R1.4). Two signals pause it:
 *
 * - **A 429 that names its wait, on a READ** — the API's own limiter
 *   (`rate_limited`, `Retry-After` in seconds) refusing a GET, or the load
 *   balancer's WAF once it answers readably (`edge_rate_limited`,
 *   `Retry-After: 60`, whatever the method: it refuses the address). The
 *   pause is the wait it names, capped at a minute. A 429 on a write (an
 *   install, a follow, a sign-in, a signing PIN) speaks for that action's
 *   own budget, not the read budget, and pauses nothing; neither does a 429
 *   that names no wait (a resend cooldown).
 * - **A run of opaque network failures** — three or more within five
 *   seconds while the browser says it is online, each from a request SENT
 *   after the last failure counted. Requests that were in flight together
 *   and fail together are one moment, not a run: a page load whose twenty
 *   parallel reads all fail counts once. A run is what a WAF block looks like
 *   when its 429 carries no CORS headers (axios sees no response at all), but
 *   so do a connectivity blip, the load balancer's CORS-less 502s while the
 *   API restarts, and a phone with no data — so this pause is short (15 s)
 *   and ends EARLY on the first readable answer of any status, or on the
 *   window's `online` event. Its notice says keepr cannot be reached, not
 *   that there are too many requests.
 *
 * While paused, the request interceptor in src/api/client.js DELAYS new GET
 * requests until the pause ends — never fails them — and sends them anyway
 * once a request has waited a minute. Other methods go straight through: a
 * person acted, and the server decides. The governor never retries anything
 * itself; a caller that wants a retry makes it, and it waits here like any
 * other read.
 *
 * `onGovernorChange` / `getGovernorState` are how the app shows ONE notice
 * while paused (components/common/RequestPauseNotice.js), instead of every
 * surface toasting its own failure. `reason` says which pause holds the reads
 * — `'rate-limited'` or `'network'` — and when both are running it is the one
 * that ends later. A rate-limited pause also says whose budget ran out
 * (`scope`), so the notice never blames the network for an account's limit
 * (KPR-989): `'account'` when the API refused a read that carried a bearer —
 * keepr-api counts a signed-in person's reads per principal, shared by every
 * tab and device — and `'network'` when the WAF refused the address, or the
 * API refused a read with no bearer (counted per address). While both kinds
 * of refusal hold the pause it is `'network'`: the WAF blocks the address
 * whoever is signed in. `scope` is null for any other state.
 */

export const MAX_PAUSE_MS = 60 * 1000;
export const MAX_DELAY_MS = 60 * 1000;
export const BURST_WINDOW_MS = 5 * 1000;
export const BURST_COUNT = 3;
// Short: most runs of no-response failures are not a block, and an early end
// needs a readable answer, which a held GET cannot fetch.
export const BURST_PAUSE_MS = 15 * 1000;
// Stamped on each request's config as it leaves the request interceptor.
export const SENT_AT = '_keeprSentAt';
// What the WAF's readable 429 says; used when its header cannot be read.
const EDGE_RETRY_AFTER_MS = 60 * 1000;

const listeners = new Set();
// Every GET sleeping in awaitClearance: woken on any change, to look again.
const wakers = new Set();
// The two pauses, kept apart so one can end without the other.
let limitedUntil = 0;
let networkUntil = 0;
// Whose budget the rate-limited pause speaks for: 'account' or 'network'.
let limitedScope = null;
let failures = [];
let lastCountedAt = null;
let resumeTimer = null;

const RESUMED = Object.freeze({ paused: false, pausedUntil: 0, reason: null, scope: null });
let snapshot = RESUMED;

const heldUntil = () => Math.max(limitedUntil, networkUntil);

const current = () => {
	const until = heldUntil();
	if (Date.now() >= until) return RESUMED;
	const reason = networkUntil > limitedUntil ? 'network' : 'rate-limited';
	return { paused: true, pausedUntil: until, reason, scope: reason === 'rate-limited' ? limitedScope : null };
};

const emit = () => {
	const next = current();
	if (next.paused === snapshot.paused && next.pausedUntil === snapshot.pausedUntil
		&& next.reason === snapshot.reason && next.scope === snapshot.scope) return;
	snapshot = next;
	wakers.forEach(wake => wake());
	listeners.forEach(listener => {
		try { listener(snapshot); } catch { /* a listener's failure is its own */ }
	});
};

// Tell the listeners when the pause is over. A timer can wake a moment early
// against the wall clock; it then waits out the rest.
const scheduleResume = () => {
	if (resumeTimer) clearTimeout(resumeTimer);
	resumeTimer = null;
	if (heldUntil() <= Date.now()) return;
	resumeTimer = setTimeout(() => {
		resumeTimer = null;
		if (Date.now() < heldUntil()) scheduleResume();
		else emit();
	}, heldUntil() - Date.now());
};

const changed = () => {
	scheduleResume();
	emit();
};

/** `{ paused, pausedUntil, reason, scope }` — the same object until the state changes. */
export function getGovernorState() {
	// Past its end but not yet announced (the resume timer is due): over.
	return snapshot.paused && Date.now() >= snapshot.pausedUntil ? RESUMED : snapshot;
}

/** Called with the new state on every pause, extension, change of reason and resume. Returns the unsubscribe. */
export function onGovernorChange(listener) {
	listeners.add(listener);
	return () => { listeners.delete(listener); };
}

// Move one pause's end later; a shorter wait never cuts a longer pause short.
// True when it moved.
function extend(why, ms) {
	const now = Date.now();
	const until = now + Math.min(Math.max(ms, 0), MAX_PAUSE_MS);
	if (until <= now) return false;
	if (why === 'network') {
		if (until <= networkUntil) return false;
		networkUntil = until;
	} else {
		if (until <= limitedUntil) return false;
		limitedUntil = until;
	}
	return true;
}

// The way to the server is open again: a network pause is over, whatever
// time it had left. A rate-limited pause keeps its Retry-After. True when a
// network pause was running.
function openNetwork() {
	failures = [];
	if (!networkUntil) return false;
	networkUntil = 0;
	return true;
}

// The browser says it is back online.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
	window.addEventListener('online', () => {
		if (openNetwork()) changed();
	});
}

const headerOf = (headers, name) => {
	if (!headers) return undefined;
	if (typeof headers.get === 'function') {
		const value = headers.get(name);
		if (value !== undefined && value !== null) return value;
	}
	const key = Object.keys(headers).find(entry => entry.toLowerCase() === name);
	return key === undefined ? undefined : headers[key];
};

/**
 * A `Retry-After` value in milliseconds from now, or null when there is none.
 * Both forms the header allows: delta-seconds ("30") and an HTTP date
 * ("Wed, 21 Oct 2026 07:28:00 GMT"). A date already past is zero.
 */
export function parseRetryAfter(value, now = Date.now()) {
	if (value === undefined || value === null) return null;
	const text = String(value).trim();
	if (!text) return null;
	if (/^\d+(\.\d+)?$/.test(text)) return Number(text) * 1000;
	const at = Date.parse(text);
	if (Number.isNaN(at)) return null;
	return Math.max(0, at - now);
}

// How long a readable 429 asks us to wait, or null when it names no wait.
function waitOf(response) {
	const header = parseRetryAfter(headerOf(response.headers, 'retry-after'));
	if (header !== null) return header;
	const data = response.data && typeof response.data === 'object' ? response.data : {};
	// The API's limiter repeats its wait in the body.
	const body = Number(data.retryAfter);
	if (data.retryAfter !== undefined && Number.isFinite(body) && body >= 0) return body * 1000;
	if (data.code === 'edge_rate_limited') return EDGE_RETRY_AFTER_MS;
	return null;
}

const online = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Is this the opaque failure a blocked request produces — no response to
 * read at all? Not a cancellation, and not a timeout: those are the
 * client's own doing.
 */
export function isOpaqueNetworkError(error) {
	if (!error || error.response) return false;
	if (error.code === 'ERR_CANCELED' || error.__CANCEL__) return false;
	return error.code === 'ERR_NETWORK' || error.message === 'Network Error';
}

// Does this refusal speak for the READ budget? The edge refuses the address
// outright; the API's limiter refusing a GET means reads are spent. A refused
// write spends only its own class (install, follow, login, attest).
function refusesReads(response) {
	const data = response?.data && typeof response.data === 'object' ? response.data : {};
	if (data.code === 'edge_rate_limited') return true;
	return String(response?.config?.method || 'get').toLowerCase() === 'get';
}

// Whose budget a read's refusal spent. The WAF counts the address; the API
// counts a request with a bearer per principal (the account, on every tab and
// device) and one without per address.
function scopeOf(response) {
	const data = response?.data && typeof response.data === 'object' ? response.data : {};
	if (data.code === 'edge_rate_limited') return 'network';
	const authorization = headerOf(response?.config?.headers, 'authorization');
	return /^Bearer\s+\S/i.test(String(authorization || '')) ? 'account' : 'network';
}

/**
 * Any readable answer, success or refusal, of any status: the way to the
 * server is open, so a network pause ends here. A 429 on a read that names
 * its wait starts (or extends) a rate-limited pause, and says whose budget
 * it speaks for.
 */
export function noteResponse(response) {
	let moved = openNetwork();
	if (response?.status === 429 && refusesReads(response)) {
		const wait = waitOf(response);
		if (wait !== null) {
			const running = Date.now() < limitedUntil;
			const scope = scopeOf(response);
			if (extend('rate-limited', wait)) moved = true;
			if (Date.now() < limitedUntil) {
				const next = (running && limitedScope === 'network') || scope === 'network' ? 'network' : 'account';
				if (next !== limitedScope) moved = true;
				limitedScope = next;
			}
		}
	}
	if (moved) changed();
}

/** A failed request, from the response interceptor. */
export function noteError(error) {
	if (error?.response) {
		noteResponse(error.response);
		return;
	}
	if (!isOpaqueNetworkError(error) || !online()) return;
	const now = Date.now();
	// Sent before the last counted failure landed, so in flight with it: the
	// same moment failing again, not the next one. (Sent in that very
	// millisecond counts as new: that is a caller reacting to the failure.)
	const sentAt = error.config?.[SENT_AT];
	if (typeof sentAt === 'number' && lastCountedAt !== null && sentAt < lastCountedAt) return;
	lastCountedAt = now;
	failures = failures.filter(at => now - at < BURST_WINDOW_MS);
	failures.push(now);
	if (failures.length >= BURST_COUNT) {
		failures = [];
		if (extend('network', BURST_PAUSE_MS)) changed();
	}
}

// Wait `ms`, or less: an abort, or any change to the pause, wakes it early.
const sleep = (ms, signal) => new Promise(resolve => {
	let timer = null;
	const done = () => {
		clearTimeout(timer);
		wakers.delete(done);
		if (signal && typeof signal.removeEventListener === 'function') signal.removeEventListener('abort', done);
		resolve();
	};
	timer = setTimeout(done, ms);
	wakers.add(done);
	if (signal && typeof signal.addEventListener === 'function') signal.addEventListener('abort', done);
});

/**
 * The request interceptor's half: a GET waits out the pause (at most
 * MAX_DELAY_MS in all, then goes anyway); anything else goes now. A request
 * aborted while it waits stops waiting — axios then cancels it unsent. Every
 * request leaves stamped with the moment it was sent (`SENT_AT`), which is
 * how noteError tells the next failure from its siblings.
 */
export async function awaitClearance(config) {
	const method = String(config?.method || 'get').toLowerCase();
	if (method === 'get') {
		const deadline = Date.now() + MAX_DELAY_MS;
		for (;;) {
			const now = Date.now();
			const until = Math.min(heldUntil(), deadline);
			if (until <= now || config?.signal?.aborted) break;
			// eslint-disable-next-line no-await-in-loop
			await sleep(until - now, config?.signal);
		}
	}
	if (config) config[SENT_AT] = Date.now();
	return config;
}

/** Tests only: forget every pause and failure, and let every waiting GET go. */
export function _resetGovernor() {
	if (resumeTimer) clearTimeout(resumeTimer);
	resumeTimer = null;
	limitedUntil = 0;
	networkUntil = 0;
	limitedScope = null;
	failures = [];
	lastCountedAt = null;
	snapshot = RESUMED;
	listeners.clear();
	[...wakers].forEach(wake => wake());
}
